import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { User } from "@travel/shared";
import { api, ApiError } from "../api/client";

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
  clear: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AUTH_QUERY_KEY = ["auth", "me"] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await api.auth.me();
        return res.user;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    retry: false,
    staleTime: Infinity,
  });

  const value: AuthContextValue = {
    user: data ?? null,
    isLoading: isPending,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
    },
    clear: () => {
      qc.setQueryData(AUTH_QUERY_KEY, null);
      qc.removeQueries({ queryKey: ["plans"] });
      qc.removeQueries({ queryKey: ["plan"] });
      qc.removeQueries({ queryKey: ["comments"] });
      qc.removeQueries({ queryKey: ["members"] });
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
